// pages/Chats/index.tsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Button,
  CircularProgress,
  Typography,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Paper,
  Divider,
  TablePagination,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CloseIcon from "@mui/icons-material/Close";
import { chatApi, chatMessageApi } from "../providers/env";
import { API_BASE } from "../providers/env";

interface Chat {
  id: number;
  session_id: string;
  pre_form_field_value: string;
  forced_transfer_count?: number | null;
  is_transfer?: boolean | null;
  created_at: string;
}

interface ChatMessage {
  id: number;
  agent_id: string;
  session_id: string;
  role: string;
  content: string;
  timestamp: string;
}

export const ChatsPage = () => {
  const [chats, setChats] = useState<Chat[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalChats, setTotalChats] = useState(0);

  // Dialog states
  const [deleteOneDialog, setDeleteOneDialog] = useState<string | null>(null);
  const [deleteAllDialog, setDeleteAllDialog] = useState(false);
  const [deleteSelectedDialog, setDeleteSelectedDialog] = useState(false);
  
  // Messages modal states
  const [messagesDialog, setMessagesDialog] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [messagesError, setMessagesError] = useState<string | null>(null);

  // Fetch all chats with pagination
  const fetchChats = async () => {
    setLoading(true);
    setError(null);
    try {
      // Get agentId from localStorage
      const agentId = localStorage.getItem("agentId");
      
      if (!agentId) {
        setError("No agent selected. Please select an agent first.");
        setChats([]);
        setTotalChats(0);
        setLoading(false);
        return;
      }

      const skip = page * rowsPerPage;
      const response = await chatApi.getChatsByAgentId({ 
        agentId,
        skip,
        limit: rowsPerPage 
      });
      
      setChats(response.data.data || []);
      setTotalChats(response.data.total || 0);
    } catch (err: any) {
      console.error("Error fetching chats:", err);
      setError("Failed to load conversations");
    } finally {
      setLoading(false);
    }
  };

  // Fetch messages for a session
  const fetchMessages = async (sessionId: string) => {
    setLoadingMessages(true);
    setMessagesError(null);
    try {
      const response = await chatMessageApi.getChatHistory({ sessionId });
      setMessages(response.data || []);
    } catch (err: any) {
      console.error("Error fetching messages:", err);
      setMessagesError("Failed to load messages");
    } finally {
      setLoadingMessages(false);
    }
  };

  // Handle view messages
  const handleViewMessages = async (sessionId: string) => {
    setSelectedSessionId(sessionId);
    setMessagesDialog(true);
    await fetchMessages(sessionId);
  };

  // Close messages dialog
  const handleCloseMessages = () => {
    setMessagesDialog(false);
    setSelectedSessionId(null);
    setMessages([]);
    setMessagesError(null);
  };

  // Handle page change
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    setSelectedIds([]); // Clear selections on page change
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page
    setSelectedIds([]); // Clear selections
  };

  useEffect(() => {
    fetchChats();
  }, [page, rowsPerPage]);

  // Handle select all checkbox (only current page)
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedIds(chats.map((chat) => chat.session_id));
    } else {
      setSelectedIds([]);
    }
  };

  // Handle individual checkbox
  const handleSelectOne = (sessionId: string) => {
    setSelectedIds((prev) =>
      prev.includes(sessionId)
        ? prev.filter((id) => id !== sessionId)
        : [...prev, sessionId]
    );
  };

  // Delete single chat
  const handleDeleteOne = async () => {
    if (!deleteOneDialog) return;

    setDeleting(true);
    try {
      await chatApi.deleteChat({ sessionId: deleteOneDialog });
      alert("Conversation deleted successfully");
      setDeleteOneDialog(null);
      setSelectedIds([]);
      fetchChats();
    } catch (err) {
      console.error("Error deleting chat:", err);
      alert("Failed to delete conversation");
    } finally {
      setDeleting(false);
    }
  };

  // Delete all chats
  const handleDeleteAll = async () => {
    setDeleting(true);
    try {
      // Get agentId from localStorage
    const agentId = localStorage.getItem("agentId");
    
    if (!agentId) {
      alert("No agent selected");
      setDeleting(false);
      return;
    }

    await chatApi.deleteAllChats({ agentId });
      alert("All conversations deleted successfully");
      setDeleteAllDialog(false);
      setSelectedIds([]);
      setPage(0); // Reset to first page
      fetchChats();
    } catch (err) {
      console.error("Error deleting all chats:", err);
      alert("Failed to delete conversations");
    } finally {
      setDeleting(false);
    }
  };

  // Delete selected chats
  const handleDeleteSelected = async () => {
    setDeleting(true);
    try {
      await chatApi.bulkDeleteChats({
        requestBody: selectedIds,
      });
      alert(`${selectedIds.length} conversations deleted successfully`);
      setDeleteSelectedDialog(false);
      setSelectedIds([]);
      fetchChats();
    } catch (err) {
      console.error("Error deleting selected chats:", err);
      alert("Failed to delete some conversations");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Card sx={{ margin: 2 }} className="chat-component">
        <CardHeader
          title="Conversations History"
          action={
            <Box display="flex" gap={2}>
              {selectedIds.length > 0 && (
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => setDeleteSelectedDialog(true)}
                  disabled={deleting}
                >
                  Delete Selected ({selectedIds.length})
                </Button>
              )}
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteAllDialog(true)}
                disabled={deleting || totalChats === 0}
              >
                Delete All
              </Button>
            </Box>
          }
        />
        <CardContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {chats.length === 0 && !loading ? (
            <Box textAlign="center" py={4}>
              <Typography variant="h6" color="text.secondary">
                No conversations found
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={
                            selectedIds.length === chats.length &&
                            chats.length > 0
                          }
                          indeterminate={
                            selectedIds.length > 0 &&
                            selectedIds.length < chats.length
                          }
                          onChange={handleSelectAll}
                        />
                      </TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Session ID</TableCell>
                      <TableCell>Pre Form Field</TableCell>
                      <TableCell>Transfer Count</TableCell>
                      <TableCell>Is Transfer</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {chats.map((chat) => (
                      <TableRow key={chat.session_id}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedIds.includes(chat.session_id)}
                            onChange={() => handleSelectOne(chat.session_id)}
                          />
                        </TableCell>
                        <TableCell>{new Date(chat.created_at).toLocaleString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}</TableCell>
                        <TableCell>{chat.session_id}</TableCell>
                        <TableCell>{chat.pre_form_field_value}</TableCell>
                        <TableCell>{chat.forced_transfer_count ?? 0}</TableCell>
                        <TableCell>{chat.is_transfer ? "Yes" : "No"}</TableCell>
                        <TableCell>
                          <Box display="flex" gap={1}>
                            <IconButton
                              color="primary"
                              onClick={() => handleViewMessages(chat.session_id)}
                              title="View Messages"
                            >
                              <VisibilityIcon />
                            </IconButton>
                            <IconButton
                              color="error"
                              onClick={() => setDeleteOneDialog(chat.session_id)}
                              disabled={deleting}
                              title="Delete Conversation"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {/* Pagination Component */}
              <TablePagination
                component="div"
                count={totalChats}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                labelRowsPerPage="Rows per page:"
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Messages Modal */}
      <Dialog 
        open={messagesDialog} 
        onClose={handleCloseMessages}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Conversation Messages
          </Typography>
          <IconButton onClick={handleCloseMessages} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {loadingMessages ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : messagesError ? (
            <Alert severity="error">{messagesError}</Alert>
          ) : messages.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography color="text.secondary">No messages found</Typography>
            </Box>
          ) : (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                Session ID: {selectedSessionId}
              </Typography>
              {messages.map((message, index) => (
                <Box key={message.id} sx={{ mb: 2 }}>
                  <Paper 
                    elevation={1} 
                    sx={{ 
                      p: 2,
                      backgroundColor: message.role === 'user' ? '#e3f2fd' : '#f5f5f5'
                    }}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography 
                        variant="subtitle2" 
                        fontWeight="bold"
                        color={message.role === 'user' ? 'primary' : 'text.secondary'}
                      >
                        {message.role.toUpperCase()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(message.timestamp).toLocaleString()}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {message.content}
                    </Typography>
                  </Paper>
                  {index < messages.length - 1 && <Divider sx={{ my: 1 }} />}
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMessages}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete One Dialog */}
      <Dialog open={!!deleteOneDialog} onClose={() => setDeleteOneDialog(null)}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WarningAmberIcon color="warning" />
          Delete Conversation
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this conversation?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOneDialog(null)}>Cancel</Button>
          <Button onClick={handleDeleteOne} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete All Dialog */}
      <Dialog open={deleteAllDialog} onClose={() => setDeleteAllDialog(false)}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WarningAmberIcon color="warning" />
          Delete All Conversations
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete ALL conversations? This action
            cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteAllDialog(false)}>Cancel</Button>
          <Button onClick={handleDeleteAll} color="error" variant="contained">
            Delete All
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Selected Dialog */}
      <Dialog
        open={deleteSelectedDialog}
        onClose={() => setDeleteSelectedDialog(false)}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WarningAmberIcon color="warning" />
          Delete Selected Conversations
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete {selectedIds.length} selected conversations?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteSelectedDialog(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteSelected}
            color="error"
            variant="contained"
          >
            Delete Selected
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};